import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  checkIsFormless,
  cleanProgramId,
  hasTerminalOutcome,
  isNonClinicalProgram,
} from "../../api/assessment";

import PAPIcon from "../../assets/PAP.svg";
import InternationalSourcingIcon from "../../assets/IntSourcing.svg";
import CopayAssistIcon from "../../assets/CoPayAssist.svg";
import ECMPIcon from "../../assets/ECMP.svg";
import GLP1Icon from "../../assets/GLP 1.svg";
import BiosimilarsIcon from "../../assets/Biosimilars.svg";
import WomensHealthIcon from "../../assets/WomensHealth.svg";
import DiabetesIcon from "../../assets/Diabetes.svg";
import WeightManagementIcon from "../../assets/WeightManagement.svg";

const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
    r / 255
  } 0 0 0 0 ${g / 255} 0 0 0 0 ${
    b / 255
  } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

const getCategoryIcon = (category) => {
  const iconStyle = {
    width: "16px",
    height: "16px",
    filter: getSvgFilter("#403C6F"),
  };

  const c = String(category || "").toLowerCase();

  if (c.includes("biosimilar")) {
    return <img src={BiosimilarsIcon} alt="Biosimilars" style={iconStyle} />;
  } else if (c.includes("pap")) {
    return <img src={PAPIcon} alt="PAP" style={iconStyle} />;
  } else if (c.includes("international") || c.includes("sourcing")) {
    return (
      <img
        src={InternationalSourcingIcon}
        alt="International Sourcing"
        style={iconStyle}
      />
    );
  } else if (c.includes("copay") || c.includes("assist")) {
    return <img src={CopayAssistIcon} alt="Copay Assist" style={iconStyle} />;
  } else if (c.includes("ecmp")) {
    return <img src={ECMPIcon} alt="ECMP" style={iconStyle} />;
  } else if (c.includes("glp") || c.includes("1")) {
    return <img src={GLP1Icon} alt="GLP-1" style={iconStyle} />;
  } else if (c.includes("women")) {
    return (
      <img src={WomensHealthIcon} alt="Women's Health" style={iconStyle} />
    );
  } else if (c.includes("diabete")) {
    return <img src={DiabetesIcon} alt="Diabetes" style={iconStyle} />;
  } else if (c.includes("weight")) {
    return (
      <img
        src={WeightManagementIcon}
        alt="Weight Management"
        style={iconStyle}
      />
    );
  }
  return null;
};

const ASSESSMENT_TYPE_OPTIONS = [
  { value: "INITIAL", label: "Initial Assessment" },
  { value: "FOLLOW_UP", label: "Follow-up Assessment" },
  { value: "DISCHARGE", label: "Discharge Assessment" },
];

const checkBiosimilar = (val) => {
  if (!val) return false;
  if (typeof val === "string") return val.toUpperCase().includes("BIOSIMILAR");
  const name = String(
    val.programDisplayName ||
      val.displayName ||
      val.name ||
      val.programName ||
      val.groupName ||
      val.category ||
      "",
  ).toUpperCase();
  const id = String(val.id || val.programId || "").toUpperCase();
  return name.includes("BIOSIMILAR") || id.includes("BIOSIMILAR");
};

const ClinicalNoteStartModal = ({
  open,
  onClose,
  onNext,
  programs = [],
  memberClinicalPrograms = [],
  availablePrograms = [],
  loading = false,
  isStarting = false,
  referenceData = null,
}) => {
  const [rows, setRows] = useState([]);
  const prevOpenRef = useRef(false);

  const primaryColor = "#403C6F";

  // Build initial rows from memberClinicalPrograms and/or programs
  useEffect(() => {
    if (open) {
      if (!prevOpenRef.current || rows.length === 0) {
        const initialRows = [];
        const seenIds = new Set();
        const seenNames = new Set();

        const candidateList = [];
        if (
          Array.isArray(memberClinicalPrograms) &&
          memberClinicalPrograms.length > 0
        ) {
          candidateList.push(...memberClinicalPrograms);
        }
        if (Array.isArray(programs) && programs.length > 0) {
          candidateList.push(...programs);
        }

        candidateList.forEach((item) => {
          if (!item || checkBiosimilar(item) || isNonClinicalProgram(item)) return;

          // Do not show programs with terminal outcomes (Member Declined, Do Not Call, Not Enrolled, Program Completion)
          if (hasTerminalOutcome(item, referenceData)) {
            return;
          }

          // Cross-check with memberClinicalPrograms if item was provided from elsewhere
          const isTerminalInMember =
            Array.isArray(memberClinicalPrograms) &&
            memberClinicalPrograms.some((mp) => {
              const mpId = mp.programId || mp.id;
              const mpName =
                mp.programDisplayName ||
                mp.programName ||
                mp.displayName ||
                mp.name ||
                mp.groupName;
              const rawId =
                item.programId || item.code || item.id || item.program;
              const name =
                item.programDisplayName ||
                item.displayName ||
                item.programName ||
                item.name ||
                item.groupName;
              const matchesId =
                mpId &&
                rawId &&
                (cleanProgramId(mpId) === cleanProgramId(rawId) ||
                  String(mpId).toUpperCase().trim() ===
                    String(rawId).toUpperCase().trim());
              const matchesName =
                mpName &&
                name &&
                String(mpName).toUpperCase().trim() ===
                  String(name).toUpperCase().trim();
              return (
                (matchesId || matchesName) &&
                hasTerminalOutcome(mp, referenceData)
              );
            });
          if (isTerminalInMember) {
            return;
          }

          // "Do not show items with assessmentOnly: true and no nextAssessmentType (completed or form-less)."
          if (item.assessmentOnly === true && !item.nextAssessmentType) {
            return;
          }

          const rawId = item.programId || item.code || item.id || item.program;
          const cleanedId =
            cleanProgramId(rawId) || String(rawId || "").toUpperCase().trim();
          const name =
            item.programDisplayName ||
            item.displayName ||
            item.programName ||
            item.name ||
            item.groupName ||
            cleanedId;
          const normName = String(name || "").toUpperCase().trim();

          if (!cleanedId || seenIds.has(cleanedId) || (normName && seenNames.has(normName))) {
            return;
          }
          seenIds.add(cleanedId);
          if (normName) seenNames.add(normName);

          const isFormless = Boolean(
            item.formless === true ||
            item.isFormless === true ||
            checkIsFormless(item, referenceData),
          );

          const isCarriedForward = Boolean(
            item.assessmentOnly && item.nextAssessmentType,
          );

          let assessmentType = "";
          if (isFormless) {
            assessmentType = "N/A";
          } else if (isCarriedForward) {
            assessmentType = item.nextAssessmentType;
          } else {
            assessmentType = (item.assessmentType || "INITIAL").toUpperCase();
          }

          initialRows.push({
            programId: cleanedId,
            programName: name,
            isFormless,
            isManual: isCarriedForward,
            isCarriedForward,
            canRemove: false,
            assessmentType,
            originalObj: item,
          });
        });

        if (initialRows.length > 0 || rows.length === 0) {
          setRows(initialRows);
        }
      }
    } else {
      setRows([]);
    }
    prevOpenRef.current = open;
  }, [open, memberClinicalPrograms, programs, referenceData]);

  const handleRowTypeChange = (programId, newType) => {
    setRows((prev) =>
      prev.map((r) =>
        r.programId === programId ? { ...r, assessmentType: newType } : r,
      ),
    );
  };

  // Validation: disable Start if any non-formless row is missing an assessment type, or if 0 rows
  const isStartDisabled = useMemo(() => {
    if (isStarting) return true;
    if (rows.length === 0) return true;
    return rows.some(
      (r) => !r.isFormless && (!r.assessmentType || r.assessmentType === ""),
    );
  }, [rows, isStarting]);

  const handleNext = async () => {
    if (isStartDisabled) return;
    const formattedPrograms = rows.map((r) => {
      if (r.isFormless) {
        return {
          programId: r.programId,
          programName: r.programName,
          isFormless: true,
        };
      }
      return {
        programId: r.programId,
        programName: r.programName,
        formType: r.assessmentType,
        assessmentType: r.assessmentType,
        ...(r.isManual ? { manuallyAdded: true } : {}),
        isFormless: false,
      };
    });
    await onNext(formattedPrograms);
  };

  const handleClose = () => {
    if (isStarting) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: { xs: "95%", sm: "600px" },
            maxWidth: "600px",
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          },
        },
      }}
    >
      <Box sx={{ position: "relative", p: 2.5 }}>
        {/* Title */}
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: "20px", color: "#10100F" }}
          >
            Start Clinical Assessment
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isStarting}
            sx={{
              color: "#403C6F",
              p: 0.5,
              "&:hover": { backgroundColor: "rgba(64,60,111,0.08)" },
            }}
            aria-label="close"
          >
            <CloseIcon sx={{ fontSize: "22px" }} />
          </IconButton>
        </DialogTitle>

        {/* Content Card */}
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E0E0E0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress size={32} sx={{ color: primaryColor }} />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: "50vh",
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
                width: "100%",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{ width: "100%", tableLayout: "auto" }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: "#CFCEDA",
                      boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
                    }}
                  >
                    <TableCell
                      sx={{
                        backgroundColor: "#CFCEDA",
                        color: "#403C6F",
                        borderColor: "#f5f5f8ff",
                        borderWidth: "0px 1px 0px 1px",
                        borderStyle: "solid",
                        fontWeight: 700,
                        fontSize: "14px",
                        py: 1.5,
                        px: 2,
                        borderBottom: "none",
                        whiteSpace: "nowrap",
                        width: "50%",
                      }}
                    >
                      Program
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#CFCEDA",
                        color: "#403C6F",
                        borderColor: "#f5f5f8ff",
                        borderWidth: "0px 1px 0px 1px",
                        borderStyle: "solid",
                        fontWeight: 700,
                        fontSize: "14px",
                        py: 1.5,
                        px: 2,
                        borderBottom: "none",
                        whiteSpace: "nowrap",
                        width: "50%",
                      }}
                    >
                      Assessment Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{
                          py: 6,
                          color: "#66638C",
                          fontSize: "14px",
                          borderBottom: "none",
                        }}
                      >
                        No programs available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const labelMap = {
                        INITIAL: "Initial Assessment",
                        FOLLOW_UP: "Follow-up Assessment",
                        DISCHARGE: "Discharge Assessment",
                        "N/A": "N/A",
                      };

                      return (
                        <TableRow
                          key={row.programId}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "#F7F8FB" },
                            "& td": { borderBottom: "1px solid #E0E0E0" },
                          }}
                        >
                          {/* Program Name with standard Halo Pill & Icon */}
                          <TableCell
                            sx={{
                              py: 1.25,
                              px: 2,
                              verticalAlign: "middle",
                              borderColor: "#E8E8E9",
                              borderWidth: "0px 0.5px 0px 0.5px",
                              borderStyle: "solid",
                              width: "50%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                border: "1px solid #ECECF1",
                                backgroundColor: "#ECECF1",
                                borderRadius: "40px",
                                px: 1.5,
                                py: 0.5,
                                height: "32px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getCategoryIcon(row.programName)}
                              <span
                                style={{
                                  color: "#1B2F3F",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  marginLeft: getCategoryIcon(row.programName)
                                    ? "8px"
                                    : "0px",
                                }}
                              >
                                {row.programName}
                              </span>
                            </Box>
                          </TableCell>

                          {/* Assessment Type */}
                          <TableCell
                            sx={{
                              py: 1.25,
                              px: 2,
                              verticalAlign: "middle",
                              borderColor: "#E8E8E9",
                              borderWidth: "0px 0.5px 0px 0.5px",
                              borderStyle: "solid",
                              width: "50%",
                            }}
                          >
                            {row.isFormless ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#7F7C9F",
                                  fontWeight: 500,
                                  fontSize: "13px",
                                }}
                              >
                                N/A (Form-less)
                              </Typography>
                            ) : (
                              <FormControl fullWidth size="small">
                                <Select
                                  displayEmpty
                                  value={row.assessmentType || ""}
                                  disabled={isStarting}
                                  onChange={(e) =>
                                    handleRowTypeChange(
                                      row.programId,
                                      e.target.value,
                                    )
                                  }
                                  MenuProps={{
                                    anchorOrigin: {
                                      vertical: "bottom",
                                      horizontal: "left",
                                    },
                                    transformOrigin: {
                                      vertical: "top",
                                      horizontal: "left",
                                    },
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#6E6D92",
                                        color: "#fff",
                                        borderRadius: "10px",
                                        boxShadow:
                                          "0px 4px 16px rgba(0,0,0,0.12)",
                                        mt: 0.5,
                                        boxSizing: "border-box",
                                        "& .MuiMenuItem-root": {
                                          fontSize: "13px",
                                          color: "#fff",
                                          py: 1,
                                          px: 2,
                                          borderRadius: "4px",
                                          mx: 0.5,
                                          my: 0.25,
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.15)",
                                          },
                                          "&.Mui-selected": {
                                            backgroundColor:
                                              "#403C6F !important",
                                            fontWeight: 600,
                                            "&:hover": {
                                              backgroundColor:
                                                "#403C6F !important",
                                            },
                                          },
                                        },
                                      },
                                    },
                                  }}
                                  sx={{
                                    borderRadius: "8px",
                                    fontSize: "13px",
                                    height: "36px",
                                    backgroundColor: "#FFFFFF",
                                    "&.Mui-disabled": {
                                      backgroundColor: "#ECECF1",
                                      cursor: "not-allowed",
                                    },
                                    "& .MuiSelect-select.Mui-disabled": {
                                      WebkitTextFillColor: "#403C6F",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: !row.assessmentType
                                        ? "#EF4444"
                                        : "#E8E8E9",
                                      borderWidth: "1px !important",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: primaryColor,
                                        borderWidth: "1px !important",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: `${primaryColor} !important`,
                                        borderWidth: "1px !important",
                                      },
                                    "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: `${primaryColor} !important`,
                                        borderWidth: "1px !important",
                                      },
                                  }}
                                  renderValue={(selected) => {
                                    if (!selected) {
                                      return (
                                        <span style={{ color: "#9F9DB6" }}>
                                          Select Assessment Type *
                                        </span>
                                      );
                                    }
                                    return labelMap[selected] || selected;
                                  }}
                                >
                                  {ASSESSMENT_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem
                                      key={opt.value}
                                      value={opt.value}
                                      sx={{ fontSize: "13px" }}
                                    >
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        {/* Modal Actions */}
        <DialogActions
          sx={{
            p: 0,
            pt: 2.5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isStarting}
            sx={{
              color: "#403C6F",
              backgroundColor: "#FFFFFF",
              border: "1px solid #403C6F",
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3.5,
              py: 0.85,
              "&:hover": {
                backgroundColor: "rgba(64, 60, 111, 0.04)",
                borderColor: "#312E5E",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            disabled={isStartDisabled}
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              color: "#FFFFFF",
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3.5,
              py: 0.85,
              minWidth: "140px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#312E5E",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E0E0E0",
                color: "#9E9E9E",
              },
            }}
          >
            {isStarting ? (
              <CircularProgress size={20} sx={{ color: "#FFF" }} />
            ) : (
              "Start Assessment"
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

ClinicalNoteStartModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  programs: PropTypes.array,
  memberClinicalPrograms: PropTypes.array,
  availablePrograms: PropTypes.array,
  loading: PropTypes.bool,
  isStarting: PropTypes.bool,
  referenceData: PropTypes.any,
};

export default ClinicalNoteStartModal;
